import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
    fetchLegalSections,
    createLegalSection,
    updateLegalSection,
    deleteLegalSection,
    selectAllLegalSections,
    selectLegalStatus,
    selectLegalSaving,
    selectLegalError,
} from './LegalSlice';
import { useIsMobile } from '../../theme';

// The markup legalToHtml.js understands, shown next to the editor so the author
// does not have to remember it. Keep in step with src/managers/Template/legalToHtml.js.
const MARKUP_HELP = [
    '## Título  →  subtítulo dentro do separador',
    '- item  →  ponto de uma lista',
    'linha em branco  →  novo parágrafo',
    '**negrito**   *itálico*   [texto](https://…)',
].join('\n');

// #region EDIT DIALOG
const SectionEditDialog = ({ section, open, saving, onClose, onSave }) => {
    const isMobile = useIsMobile();
    const [form, setForm] = useState(null);

    // Reload the form whenever a different section is opened. A new section is
    // the `{}` sentinel: same dialog, empty fields, hidden by default.
    useEffect(() => {
        if (!section) return setForm(null);
        setForm({
            title: section.title || '',
            content: section.content || '',
            isVisible: !!section.isVisible,
        });
    }, [section]);

    if (!section || !form) return null;

    const isNew = !section.idLegalSection;

    const setField = (field) => (event) => {
        const value = field === 'isVisible' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        if (!form.title.trim()) return;

        if (isNew) return onSave(null, { title: form.title.trim(), content: form.content, isVisible: form.isVisible });

        // Send only what actually changed, so an untouched field is never
        // written — and an edit that changes nothing does not rebuild the site.
        const changes = {};
        if (form.title.trim() !== (section.title || '')) changes.title = form.title.trim();
        if (form.content !== (section.content || '')) changes.content = form.content;
        if (form.isVisible !== !!section.isVisible) changes.isVisible = form.isVisible;

        if (!Object.keys(changes).length) return onClose();
        onSave(section.idLegalSection, changes);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ pb: 1 }}>
                {isNew ? 'Novo separador' : section.title}
                <Typography variant='caption' color='text.secondary' component='div'>
                    Aparece no modal “Termos e Políticas”, no rodapé do site
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        label='Título do separador'
                        helperText='O texto do separador dentro do modal (ex.: “Política de Privacidade”)'
                        value={form.title}
                        onChange={setField('title')}
                        error={!form.title.trim()}
                        fullWidth
                        autoFocus={isNew}
                    />
                    <TextField
                        label='Texto'
                        value={form.content}
                        onChange={setField('content')}
                        fullWidth
                        multiline
                        minRows={16}
                        // Monospaced so the "## " and "- " prefixes line up and
                        // are easy to spot in a long text.
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 1.6 } }}
                        helperText={
                            <Box component='span' sx={{ whiteSpace: 'pre-line', display: 'block' }}>
                                {MARKUP_HELP}
                            </Box>
                        }
                    />

                    <Divider />
                    <FormControlLabel
                        control={<Switch checked={form.isVisible} onChange={setField('isVisible')} />}
                        label='Ativo (o separador aparece no site)'
                    />
                    <Typography variant='caption' color='text.secondary'>
                        Desativar retira o separador do site sem apagar o texto. Guardar reconstrói o site inteiro — o rodapé faz parte de
                        todas as páginas.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
                <Button onClick={onClose} disabled={saving} color='inherit'>
                    Cancelar
                </Button>
                <Button onClick={handleSave} variant='contained' disabled={saving || !form.title.trim()}>
                    {saving ? 'A guardar…' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// #region ROW
// One section in the list. The arrows swap this row's sort_order with its
// neighbour's — the tab order in the modal is exactly this order.
const SectionRow = ({ section, isFirst, isLast, disabled, onEdit, onToggle, onMove, onDelete }) => (
    <Paper
        variant='outlined'
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: { xs: 1, md: 1.5 },
            borderLeft: 4,
            borderLeftColor: section.isVisible ? 'success.main' : 'divider',
        }}
    >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <IconButton size='small' disabled={disabled || isFirst} onClick={() => onMove(section, -1)} aria-label='Subir'>
                <ArrowUpwardIcon fontSize='inherit' />
            </IconButton>
            <IconButton size='small' disabled={disabled || isLast} onClick={() => onMove(section, 1)} aria-label='Descer'>
                <ArrowDownwardIcon fontSize='inherit' />
            </IconButton>
        </Box>

        <Box
            onClick={() => onEdit(section)}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onEdit(section)}
            sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600 }}>{section.title}</Typography>
                <Chip
                    size='small'
                    variant='outlined'
                    color={section.isVisible ? 'success' : 'default'}
                    label={section.isVisible ? 'NO SITE' : 'DESATIVADO'}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                />
            </Box>
            <Typography variant='body2' color='text.secondary' noWrap sx={{ mt: 0.25 }}>
                {(section.content || '').replace(/^#+\s*/gm, '').replace(/\s+/g, ' ').trim() || 'Sem texto'}
            </Typography>
        </Box>

        <Tooltip title={section.isVisible ? 'Desativar' : 'Ativar'}>
            <span>
                <Switch
                    size='small'
                    checked={!!section.isVisible}
                    disabled={disabled}
                    onChange={(e) => onToggle(section, e.target.checked)}
                    inputProps={{ 'aria-label': 'Ativo no site' }}
                />
            </span>
        </Tooltip>
        <IconButton color='primary' onClick={() => onEdit(section)} aria-label='Editar separador'>
            <EditIcon />
        </IconButton>
        <IconButton color='error' disabled={disabled} onClick={() => onDelete(section)} aria-label='Apagar separador'>
            <DeleteOutlineIcon />
        </IconButton>
    </Paper>
);

// #region LEGAL
const Legal = () => {
    const dispatch = useDispatch();

    const sections = useSelector(selectAllLegalSections);
    const status = useSelector(selectLegalStatus);
    const saving = useSelector(selectLegalSaving);
    const error = useSelector(selectLegalError);

    const [editing, setEditing] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        dispatch(fetchLegalSections());
    }, [dispatch]);

    useEffect(() => {
        if (error) setSnackbar({ open: true, message: error, severity: 'error' });
    }, [error]);

    const notify = (message) => setSnackbar({ open: true, message, severity: 'success' });

    const handleSave = useCallback(
        async (id, changes) => {
            const result = id ? await dispatch(updateLegalSection({ id, changes })) : await dispatch(createLegalSection(changes));

            if (updateLegalSection.fulfilled.match(result) || createLegalSection.fulfilled.match(result)) {
                setEditing(null);
                notify(id ? 'Separador guardado — site reconstruído' : 'Separador criado');
            }
        },
        [dispatch]
    );

    const handleToggle = useCallback(
        async (section, isVisible) => {
            const result = await dispatch(updateLegalSection({ id: section.idLegalSection, changes: { isVisible } }));
            if (updateLegalSection.fulfilled.match(result)) {
                notify(isVisible ? `“${section.title}” ativado no site` : `“${section.title}” desativado`);
            }
        },
        [dispatch]
    );

    // Reorder by swapping the two rows' sortOrder values. Two PATCHes, so two
    // full renders — acceptable for something changed this rarely, and it keeps
    // the API a plain field update rather than a bespoke reorder endpoint.
    const handleMove = useCallback(
        async (section, direction) => {
            const index = sections.findIndex((s) => s.idLegalSection === section.idLegalSection);
            const neighbour = sections[index + direction];
            if (!neighbour) return;

            // Equal sortOrder values (or a fresh list of them) would make the
            // swap a no-op, so fall back to the positions themselves.
            const a = section.sortOrder === neighbour.sortOrder ? index : section.sortOrder;
            const b = section.sortOrder === neighbour.sortOrder ? index + direction : neighbour.sortOrder;

            await dispatch(updateLegalSection({ id: section.idLegalSection, changes: { sortOrder: b } }));
            await dispatch(updateLegalSection({ id: neighbour.idLegalSection, changes: { sortOrder: a } }));
        },
        [dispatch, sections]
    );

    const handleDelete = useCallback(async () => {
        const section = confirmDelete;
        setConfirmDelete(null);
        const result = await dispatch(deleteLegalSection(section.idLegalSection));
        if (deleteLegalSection.fulfilled.match(result)) notify(`“${section.title}” apagado`);
    }, [confirmDelete, dispatch]);

    // #region RETURN
    return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: { xs: 1, md: 2 }, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='caption' color='text.secondary' sx={{ flex: 1 }}>
                    Os separadores do modal “Termos e Políticas”, aberto pelo rodapé do site. A ordem aqui é a ordem dos separadores.
                    Guardar reconstrói o site de imediato — sem reinício.
                </Typography>
                {status === 'loading' && <CircularProgress size={14} />}
                <Button variant='contained' size='small' startIcon={<AddIcon />} onClick={() => setEditing({})} disabled={saving}>
                    Novo
                </Button>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pb: 2 }}>
                {sections.map((section, index) => (
                    <SectionRow
                        key={section.idLegalSection}
                        section={section}
                        isFirst={index === 0}
                        isLast={index === sections.length - 1}
                        disabled={saving}
                        onEdit={setEditing}
                        onToggle={handleToggle}
                        onMove={handleMove}
                        onDelete={setConfirmDelete}
                    />
                ))}
                {!sections.length && status !== 'loading' && (
                    <Paper variant='outlined' sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color='text.secondary'>Sem separadores. O link no rodapé só aparece quando existe pelo menos um ativo.</Typography>
                    </Paper>
                )}
            </Box>

            <SectionEditDialog section={editing} open={!!editing} saving={saving} onClose={() => setEditing(null)} onSave={handleSave} />

            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Apagar “{confirmDelete?.title}”?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        O texto é apagado definitivamente. Para retirar o separador do site mantendo o texto, desative-o.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)} color='inherit'>
                        Cancelar
                    </Button>
                    <Button onClick={handleDelete} color='error' variant='contained'>
                        Apagar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Legal;
